import { memo, useEffect, useRef, useState } from 'react';
import { IconSpin, IconStop, IconTurbo } from '../icons';

// ── Turbo badge geometry ──────────────────────────────────────────
// The badge lives behind the main button, offset to the upper-right
// far enough that its lightning icon pokes out past the main circle's
// edge. Expressed as fractions of `size` so the layout scales cleanly.
//   TURBO_D_FRAC    — distance from main center to badge center.
//                     Must be > 0.5 + TURBO_SIZE_FRAC*TURBO_ICON_FRAC/2
//                     so the icon itself sits outside the main circle.
//   TURBO_SIZE_FRAC — badge diameter.
//   TURBO_ICON_FRAC — lightning size as a fraction of the badge.
const TURBO_D_FRAC = 0.62;
const TURBO_SIZE_FRAC = 0.32;
const TURBO_ICON_FRAC = 0.55;
const TURBO_ANGLE = -Math.PI / 4; // upper-right in SVG y-down coords

export interface SpinButtonProps {
  size?: number;
  onClick?: () => void;
  /** Controlled spinning state. If omitted, the button manages its own toggle. */
  spinning?: boolean;
  onToggle?: (next: boolean) => void;
  /**
   * Controlled turbo mode. When true, a lightning badge appears
   * behind the button, offset to the upper-right. If omitted, the
   * button manages its own toggle locally so a standalone SpinButton
   * (outside SlotUI) still supports the gesture.
   */
  turbo?: boolean;
  /**
   * Fires when the user long-presses the button. Use this to flip your
   * turbo state and optionally bridge `ui.turboChange` to the hosted
   * game. The callback receives the intended new value (the opposite
   * of `turbo`).
   */
  onTurboToggle?: (next: boolean) => void;
  /** How long the user must hold before the long-press fires. Default: 450ms. */
  longPressMs?: number;
  /**
   * Renders the button dimmed and inert — no click, no long-press. Used
   * when the hosted game has taken exclusive control of the reel canvas
   * (`game.disableSpinButton`). Default: `false`.
   */
  disabled?: boolean;
}

type BouncePhase = 'idle' | 'down' | 'up';

// Memoized — SlotUI already passes stable references for every prop here
// (spinning/turbo/disabled are primitives, onClick/onTurboToggle are
// useCallback'd), so this actually bails out on the high-frequency
// swipe-drag re-renders that would otherwise re-run this component's own
// gesture-handling setup for no reason.
export const SpinButton = memo(function SpinButton({
  size = 92,
  onClick,
  spinning: spinningProp,
  onToggle,
  turbo: turboProp,
  onTurboToggle,
  longPressMs = 450,
  disabled = false,
}: SpinButtonProps) {
  const [selfSpin, setSelfSpin] = useState(false);
  const [selfTurbo, setSelfTurbo] = useState(false);
  const [bouncePhase, setBouncePhase] = useState<BouncePhase>('idle');
  // Two-value state for the press affordance: 'idle' (ring hidden, fast
  // fade transition), 'pressing' (ring fills, no transition while we
  // mutate strokeDashoffset every frame). We don't track sub-progress in
  // React — that lives in the DOM via the ref below.
  const [pressing, setPressing] = useState(false);
  const spinning = spinningProp !== undefined ? spinningProp : selfSpin;
  const turbo = turboProp !== undefined ? turboProp : selfTurbo;

  // ── Long-press detection ──────────────────────────────────────────
  // Uses pointer events so touch, mouse, and pen all share one code path.
  // We track whether the long-press fired so the subsequent `click`
  // event (which browsers always dispatch after a successful
  // pointerdown→pointerup inside the element) can be suppressed — we
  // don't want a turbo toggle to also trigger a spin.
  const pressStartRef = useRef<number | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const rafRef = useRef<number | null>(null);
  const longPressFiredRef = useRef(false);
  // Long-press progress (0..1) was previously a React state updated each
  // rAF tick, which forced a full re-render at ~60fps for the entire 450ms
  // press. On iOS, that competes for main-thread time with whatever the
  // reels iframe is doing. We now update the SVG circle's
  // stroke-dashoffset + opacity directly via a ref — visually identical,
  // zero React work during the gesture.
  const progressCircleRef = useRef<SVGCircleElement | null>(null);

  // Ring geometry — hoisted up here (rather than next to the SVG markup)
  // so `setRingProgress` can reference `ringCircumference` without a
  // "used before declaration" issue. Recomputed each render off `size`.
  const ringSize = size + 16;
  const ringRadius = (size + 12) / 2;
  const ringCircumference = 2 * Math.PI * ringRadius;

  const setRingProgress = (p: number) => {
    const el = progressCircleRef.current;
    if (!el) return;
    el.style.strokeDashoffset = String(ringCircumference * (1 - p));
    el.style.opacity = p > 0 ? '1' : '0';
  };

  const clearPress = () => {
    if (timerRef.current != null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    pressStartRef.current = null;
    setRingProgress(0);
    setPressing(false);
  };

  // Cleanup any in-flight timers if the component unmounts mid-press.
  useEffect(() => () => clearPress(), []);

  const handlePointerDown = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (disabled) return;
    // Ignore secondary mouse buttons — only left-click / primary touch
    // should trigger either a spin or a turbo toggle.
    if (e.button !== 0 && e.pointerType === 'mouse') return;
    longPressFiredRef.current = false;
    pressStartRef.current = performance.now();
    setPressing(true);
    setRingProgress(0);
    timerRef.current = setTimeout(() => {
      longPressFiredRef.current = true;
      const next = !turbo;
      if (turboProp === undefined) setSelfTurbo(next);
      onTurboToggle?.(next);
      // Haptic cue on devices that support it — this is a "something
      // happened" beat that pairs with the badge appearing.
      if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
        try { navigator.vibrate?.(15); } catch { /* iOS throws */ }
      }
      clearPress();
    }, longPressMs);

    const tick = () => {
      if (pressStartRef.current == null) return;
      const elapsed = performance.now() - pressStartRef.current;
      // Update the SVG circle directly — no React render this frame.
      setRingProgress(Math.min(1, elapsed / longPressMs));
      if (elapsed < longPressMs) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  };

  const handlePointerCancel = () => clearPress();
  const handlePointerLeave = () => clearPress();
  const handlePointerUp = () => clearPress();

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (disabled) return;
    // The long-press already fired a turbo toggle — eat the click so
    // the spin isn't accidentally triggered on pointerup.
    if (longPressFiredRef.current) {
      longPressFiredRef.current = false;
      e.preventDefault();
      return;
    }
    if (onToggle) onToggle(!spinning);
    else setSelfSpin((s) => !s);
    onClick?.();
    setBouncePhase('down');
    setTimeout(() => setBouncePhase('up'), 110);
    setTimeout(() => setBouncePhase('idle'), 330);
  };

  // Also block the synthetic click the browser fires after a successful
  // contextmenu on some desktop setups — belt and suspenders.
  const handleContextMenu = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (longPressFiredRef.current) e.preventDefault();
  };

  const iconSize = size * 0.75;
  const ringScale =
    bouncePhase === 'down' ? 0.88 : bouncePhase === 'up' ? 1.06 : 1;
  const btnScale =
    bouncePhase === 'down' ? 0.93 : bouncePhase === 'up' ? 1.03 : 1;

  // ── Turbo badge placement ────────────────────────────────────────
  const turboSize = size * TURBO_SIZE_FRAC;
  const turboD = size * TURBO_D_FRAC;
  const turboOffsetX = turboD * Math.cos(TURBO_ANGLE);
  const turboOffsetY = turboD * Math.sin(TURBO_ANGLE);
  // How far the badge pokes past the main button's bounding box —
  // used to grow the container so the visible crescent isn't clipped.
  const turboOverflow = Math.max(
    0,
    Math.abs(turboOffsetX) + turboSize / 2 - size / 2,
    Math.abs(turboOffsetY) + turboSize / 2 - size / 2,
  );
  const containerPad = Math.max(turboOverflow + 4, 10);
  const containerSize = size + containerPad * 2;

  // ── Long-press progress ring ──────────────────────────────────────
  // Sits just outside the main button so the fill reads as "you're
  // about to flip turbo." Drawn inside the button with overflow:
  // visible so it can extend past the circle edge.
  // (Geometry is computed earlier — see ringSize / ringRadius /
  // ringCircumference declarations next to setRingProgress.)

  return (
    <div
      style={{
        position: 'relative',
        width: containerSize,
        height: containerSize,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {/* Turbo badge — lives behind the main button (earlier in the
          DOM → lower stacking order by default, also explicitly
          zIndex: 0). Offset to the upper-right so the lightning pokes
          out past the main circle's edge and stays visible. */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          left: '50%',
          top: '50%',
          width: turboSize,
          height: turboSize,
          borderRadius: '50%',
          background: 'rgba(254,55,180,0.15)',
          border: '2px solid #FE37B4',
          // Centering translate first, then the offset translate,
          // then scale the pop-in animation.
          transform:
            `translate(-50%, -50%) ` +
            `translate(${turboOffsetX}px, ${turboOffsetY}px) ` +
            `scale(${turbo ? 1 : 0.4})`,
          transformOrigin: 'center',
          opacity: turbo ? 1 : 0,
          transition:
            'opacity 0.22s cubic-bezier(0.22, 1, 0.36, 1), transform 0.28s cubic-bezier(0.22, 1, 0.36, 1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#FE37B4',
          boxShadow: '0 2px 10px rgba(0,0,0,0.45)',
          pointerEvents: 'none',
          zIndex: 0,
        }}
      >
        <IconTurbo size={turboSize * TURBO_ICON_FRAC} />
      </div>

      {/* Main spin button — sits on top of the turbo badge. All gesture
          logic (click, long-press, bounce) stays attached to this
          element; the badge is purely decorative. */}
      <button
        onClick={handleClick}
        onContextMenu={handleContextMenu}
        onPointerDown={handlePointerDown}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerLeave}
        onPointerCancel={handlePointerCancel}
        disabled={disabled}
        aria-label={
          spinning
            ? 'Stop'
            : turbo
            ? 'Spin (turbo on, long-press to disable)'
            : 'Spin (long-press for turbo)'
        }
        aria-pressed={turbo}
        style={{
          position: 'relative',
          width: size,
          height: size,
          borderRadius: '50%',
          background: 'radial-gradient(70.95% 70.95% at 19.52% 9.05%, #FFFFFF 55.13%, #F1F1F1 73.73%)',
          color: '#FE37B4',
          border: 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.4 : 1,
          boxShadow:
            '0 6px 24px rgba(0,0,0,0.35), 0 0 0 1px #FFFFFF, 0 0 0 5px var(--border-2)',
          transform: `scale(${btnScale})`,
          transformOrigin: 'center',
          transition:
            bouncePhase === 'idle'
              ? 'transform 0.2s cubic-bezier(0.37, 0, 0.63, 1), opacity 0.15s ease'
              : 'transform 0.14s cubic-bezier(0.37, 0, 0.63, 1), opacity 0.15s ease',
          padding: 0,
          outline: 'none',
          overflow: 'visible',
          WebkitTouchCallout: 'none',
          touchAction: 'manipulation',
          userSelect: 'none',
          zIndex: 1,
        }}
      >
        {/* Spin / Stop icon cross-fade. Wrapped in a clipped circle so
            the rotating glyph can't bleed past the button edge. */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            borderRadius: '50%',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: spinning ? 0 : 1,
              transform: spinning
                ? 'rotate(180deg) scale(0.6)'
                : 'rotate(0deg) scale(1)',
              transition:
                'opacity 0.25s ease, transform 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
          >
            <IconSpin size={iconSize} />
          </div>
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: spinning ? 1 : 0,
              transform: spinning
                ? 'rotate(0deg) scale(1)'
                : 'rotate(-180deg) scale(0.6)',
              transition:
                'opacity 0.25s ease, transform 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
          >
            <IconStop size={iconSize * 1.28} />
          </div>
        </div>

        {/* Long-press progress ring — a thin arc that fills around the
            button as the user holds. Stroke-dashoffset animates from
            full circumference (invisible) to 0 (full ring). */}
        <svg
          width={ringSize}
          height={ringSize}
          style={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            transform: `translate(-50%, -50%) scale(${ringScale}) rotate(-90deg)`,
            transformOrigin: 'center',
            transition:
              bouncePhase === 'idle'
                ? 'transform 0.22s cubic-bezier(0.37, 0, 0.63, 1)'
                : 'transform 0.14s cubic-bezier(0.37, 0, 0.63, 1)',
            pointerEvents: 'none',
            overflow: 'visible',
          }}
        >
          <circle
            ref={progressCircleRef}
            cx={ringSize / 2}
            cy={ringSize / 2}
            r={ringRadius}
            fill="none"
            stroke="#FE37B4"
            strokeWidth="3"
            strokeLinecap="round"
            strokeDasharray={ringCircumference}
            // strokeDashoffset + opacity are written directly to the
            // DOM by setRingProgress every rAF tick during a press —
            // bypassing React keeps the gesture at 60fps even when the
            // hosted iframe game is busy painting. We seed the initial
            // values here for the first paint before any press begins.
            strokeDashoffset={ringCircumference}
            style={{
              opacity: 0,
              // While we're mutating strokeDashoffset every frame we
              // need transition:none on the offset; the fade-out after
              // release wants a quick opacity tween. We toggle the
              // transition mode based on the React-level `pressing`
              // bool (set/unset just twice per gesture).
              transition: pressing ? 'none' : 'opacity 0.18s ease',
            }}
          />
        </svg>
      </button>
    </div>
  );
});
